import { nearestOnRoute } from './route';

/** Stops further than this from their direction's route are flagged. */
const FAR_M = 50;
/** A stop this much before the previous one along the route is out of order. */
const BACK_M = 25;

type Stations = Map<string, { name: string; geometry: { coordinates: number[] } }>;

/** The route from the first stop's end to the last stop's end (reversed when drawn the other way). */
export function orientRoute(route: number[][] | undefined, ordered: string[], stations: Stations) {
  const first = stations.get(ordered[0] ?? '')?.geometry.coordinates;
  const last = stations.get(ordered.at(-1) ?? '')?.geometry.coordinates;
  if (!route || route.length < 2 || !first || !last || ordered.length < 2) return route;
  return nearestOnRoute(route, first).along > nearestOnRoute(route, last).along
    ? [...route].reverse()
    : route;
}

/**
 * A direction's stops in order, with where each meets the route and what looks wrong (far from
 * the route, out of order). The route is taken in travel order: old routes are sometimes drawn from
 * the last stop, which would otherwise make every stop look out of order.
 */
export function checkStops(route: number[][] | undefined, ordered: string[], stations: Stations) {
  const path = orientRoute(route, ordered, stations);
  let previous = -Infinity;
  return ordered.map((id) => {
    const s = stations.get(id);
    const position = s?.geometry.coordinates as [number, number] | undefined;
    const hit = position && path && path.length >= 2 ? nearestOnRoute(path, position) : null;
    const outOfOrder = !!hit && hit.along < previous - BACK_M;
    if (hit) previous = Math.max(previous, hit.along);
    return {
      id,
      name: s?.name ?? id,
      position,
      onRoute: hit && hit.distance > 15 ? hit.point : undefined,
      distance: hit ? Math.round(hit.distance) : 0,
      far: !!hit && hit.distance > FAR_M,
      outOfOrder,
      warn: !!hit && (hit.distance > FAR_M || outOfOrder),
    };
  });
}
